import React from 'react';
import { Return } from 'types/return';
import { getStoreCurrencyFormatter } from 'utils';

type DefectResolutionPanelProps = {
  returnRecord: Pick<Return, 'settlementType' | 'settlementStatus' | 'meta'>;
  fallbackCurrency?: string;
};

const SETTLEMENT_LABELS: Record<string, string> = {
  COMPLETED: 'issued',
  PROCESSING: 'in progress',
  FAILED: 'failed',
  OUTCOME_UNKNOWN: 'needs verification',
};

const currencyCode = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const normalized = value.trim().toUpperCase();
  return /^[A-Z]{3}$/.test(normalized) ? normalized : null;
};

export const DefectResolutionPanel = ({
  returnRecord,
  fallbackCurrency,
}: DefectResolutionPanelProps) => {
  const { settlementType, settlementStatus, meta } = returnRecord;
  if (!['REFUND', 'STORE_CREDIT'].includes(settlementType || '')) return null;
  const completed = settlementStatus === 'COMPLETED';
  // Scheduled checks can infer a refund type before a merchant selects one.
  if (!completed && meta?.settlement?.trigger !== 'MANUAL_ACTION') return null;
  const statusLabel = SETTLEMENT_LABELS[settlementStatus || ''];
  if (!statusLabel) return null;

  const isRefund = settlementType === 'REFUND';
  const amount = isRefund ? meta?.refund?.total : meta?.storeCreditAmount;
  const numericAmount = Number(amount);
  const hasAmount =
    (typeof amount === 'number' ||
      (typeof amount === 'string' && amount.trim() !== '')) &&
    Number.isFinite(numericAmount) && numericAmount >= 0;
  const receiptCurrency = isRefund
    ? meta?.refund?.currencyCode
    : meta?.storeCreditCurrency;
  const currency = currencyCode(receiptCurrency) ?? currencyCode(fallbackCurrency);

  return (
    <section aria-labelledby="defect-resolution-title" className="rounded-lg border border-gray-200 bg-white p-6">
      <h3 id="defect-resolution-title" className="font-semibold text-gray-900">Merchant resolution</h3>
      <p className="mt-3 font-medium text-gray-900">
        {isRefund ? 'Refund' : 'Store credit'} {statusLabel}
      </p>
      {completed && (
        <p className="mt-2 text-lg font-semibold text-gray-900">
          {!hasAmount
            ? 'Recorded amount unavailable.'
            : currency
            ? getStoreCurrencyFormatter(currency, numericAmount)
            : 'Recorded currency unavailable.'}
        </p>
      )}
    </section>
  );
};
